import React, { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Download,
  Trash2,
  AlertTriangle,
  FileJson,
  FileSpreadsheet,
  CheckCircle,
  User,
  Phone,
  Save,
  Loader2,
} from "lucide-react";
import { useLocation } from "wouter";
import { useSearch } from "wouter";
import { toast } from "sonner";
import EntityManagement from "./EntityManagement";
import FxRateManagement from "./FxRateManagement";
import { AccountReconciliationPanel } from "@/components/AccountReconciliationPanel";
import { BUILD_INFO } from "@shared/releaseInfo";

export default function AccountSettings() {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const [, navigate] = useLocation();
  const search = useSearch();
  const isRTL = language === "ar";
  type SettingsTab = "profile" | "balance" | "entities" | "fx" | "privacy";
  const workspace = trpc.auth.workspace.useQuery();
  const isBusinessWorkspace = workspace.data?.activeWorkspace === "business" && workspace.data.businessAvailable === true;
  const isIndividualWorkspace = workspace.data?.activeWorkspace === "individual";
  const visibleTabs: SettingsTab[] = isBusinessWorkspace ? ["profile", "entities", "fx", "privacy"] : ["profile", "balance", "fx", "privacy"];
  const requestedTab = new URLSearchParams(search).get("tab");
  const validTab = (value: string | null): SettingsTab => value === "balance" || value === "entities" || value === "fx" || value === "privacy" ? value : "profile";
  const resolveVisibleTab = (value: string | null): SettingsTab => {
    const tab = validTab(value);
    return (isBusinessWorkspace && tab === "balance") || (!isBusinessWorkspace && tab === "entities") ? "profile" : tab;
  };
  const [activeTab, setActiveTab] = useState<SettingsTab>(() => resolveVisibleTab(requestedTab));

  // Profile state
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [profileDirty, setProfileDirty] = useState(false);

  const { data: profile } = trpc.profile.get.useQuery();
  const businessFeature = trpc.feature.businessStatus.useQuery();
  const utils = trpc.useUtils();

  useEffect(() => {
    if (profile) {
      setName(profile.name ?? "");
      setPhone(profile.phone ?? "");
    }
  }, [profile?.name, profile?.phone]);

  useEffect(() => {
    setActiveTab(resolveVisibleTab(requestedTab));
  }, [requestedTab, isBusinessWorkspace]);

  useEffect(() => {
    if (!isBusinessWorkspace && validTab(requestedTab) === "entities") {
      navigate("/account");
    }
  }, [isBusinessWorkspace, navigate, requestedTab]);

  const selectTab = (tab: SettingsTab) => {
    if ((!isBusinessWorkspace && tab === "entities") || (isBusinessWorkspace && tab === "balance")) return;
    setActiveTab(tab);
    navigate(tab === "profile" ? "/account" : `/account?tab=${tab}`);
  };

  const profileMutation = trpc.profile.update.useMutation({
    onSuccess: () => {
      toast.success(isRTL ? "تم حفظ الملف الشخصي بنجاح!" : "Profile saved successfully!");
      utils.profile.get.invalidate();
      utils.auth.me.invalidate();
      setProfileDirty(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(isRTL ? "يرجى إدخال اسمك" : "Please enter your name");
      return;
    }
    profileMutation.mutate({ name: name.trim(), phone: phone.trim() || undefined });
  };

  // Export/Delete state
  const [confirmWord, setConfirmWord] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [showBusinessClearConfirm, setShowBusinessClearConfirm] = useState(false);
  const [businessClearName, setBusinessClearName] = useState("");

  const { data: exportData, isLoading: exportLoading, refetch: fetchExport } = trpc.account.exportAllData.useQuery(undefined, { enabled: false });
  const { data: deletionSummary } = trpc.account.deletionSummary.useQuery(undefined, { enabled: showDeleteConfirm });
  const { data: businessClearPreview, isLoading: businessClearPreviewLoading } = trpc.businessData.clearPreview.useQuery(undefined, { enabled: isBusinessWorkspace && showBusinessClearConfirm });
  const clearBusinessData = trpc.businessData.clear.useMutation({
    onSuccess: async (result) => {
      setShowBusinessClearConfirm(false);
      setBusinessClearName("");
      await utils.invalidate();
      if (result.storageFilesFailed > 0) toast.error(t("businessDataClear.storageIncomplete"));
      else toast.success(t("businessDataClear.success"));
    },
    onError: (error) => toast.error(error.message === "BUSINESS_DATA_CLEAR_NAME_MISMATCH" ? t("businessDataClear.nameMismatch") : t("businessDataClear.failed")),
  });
  const deleteAccount = trpc.account.deleteAccount.useMutation({
    onSuccess: () => {
      setDeleted(true);
      setTimeout(() => {
        window.location.href = "/";
      }, 5000);
    },
  });

  const handleExportJSON = async () => {
    const result = await fetchExport();
    if (result.data) {
      const blob = new Blob([JSON.stringify(result.data.json, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mizano-data-export-${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleExportCSV = async () => {
    const result = await fetchExport();
    if (result.data) {
      const blob = new Blob([result.data.csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mizano-transactions-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleDelete = () => {
    if (confirmWord === "DELETE") {
      deleteAccount.mutate({ confirmationWord: "DELETE" });
    }
  };

  const confirmBusinessDataClear = () => {
    if (!businessClearPreview || businessClearName !== businessClearPreview.entity.name) return;
    clearBusinessData.mutate({ confirmationName: businessClearName });
  };

  if (deleted) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" dir={isRTL ? "rtl" : "ltr"}>
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <h2 className="text-xl font-bold">
              {isRTL ? "تم حذف حسابك" : "Account Deleted"}
            </h2>
            <p className="text-muted-foreground">
              {isRTL
                ? "تم حذف جميع بياناتك بنجاح. سيتم إعادة توجيهك إلى الصفحة الرئيسية خلال ثوانٍ."
                : "All your data has been successfully deleted. You will be redirected to the home page in a few seconds."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-3xl mx-auto" dir={isRTL ? "rtl" : "ltr"}>
      <div>
        <h1 className="text-2xl font-bold">
          {isRTL ? "إعدادات الحساب" : "Account Settings"}
        </h1>
        <p className="text-muted-foreground mt-1">
          {isRTL ? "إدارة ملفك الشخصي وبياناتك وحسابك" : "Manage your profile, data, and account"}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 rounded-xl border border-border bg-card p-2 sm:flex sm:flex-wrap" role="tablist" aria-label={t("settings.tabsLabel")}>
        {visibleTabs.map((tab) => <Button key={tab} type="button" variant={activeTab === tab ? "default" : "ghost"} className="min-h-11 justify-center" role="tab" aria-selected={activeTab === tab} onClick={() => selectTab(tab)}>{t(`settings.tab.${tab}`)}</Button>)}
      </div>

      {activeTab === "profile" ? <>
      {/* Profile / Account Setup Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            {isRTL ? "إعداد الحساب" : "Account Setup"}
          </CardTitle>
          <CardDescription>
            {isRTL
              ? "قم بتحديث اسمك ورقم هاتفك المرتبط بحساباتك البنكية."
              : "Update your name and the phone number linked to your bank accounts."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {isRTL ? "الاسم الكامل" : "Full Name"}
              </Label>
              <Input
                id="name"
                placeholder={isRTL ? "أدخل اسمك الكامل" : "Enter your full name"}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setProfileDirty(true);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                {isRTL ? "رقم الهاتف" : "Phone Number"}
                <span className="text-muted-foreground text-xs font-normal">
                  ({isRTL ? "اختياري" : "Optional"})
                </span>
              </Label>
              <Input
                id="phone"
                placeholder="+965 XXXX XXXX"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  setProfileDirty(true);
                }}
                type="tel"
              />
              <p className="text-xs text-muted-foreground">
                {isRTL
                  ? "هذا هو رقم الهاتف المرتبط بحساباتك البنكية."
                  : "This is the phone number linked to your bank accounts."}
              </p>
            </div>
            <Button
              type="submit"
              disabled={profileMutation.isPending || !profileDirty}
              className="flex items-center gap-2"
            >
              {profileMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {profileMutation.isPending
                ? (isRTL ? "جارٍ الحفظ..." : "Saving...")
                : (isRTL ? "حفظ التغييرات" : "Save Changes")}
            </Button>
          </form>
        </CardContent>
      </Card>

      {user?.role === "admin" ? <p data-testid="build-info" className="px-1 text-xs text-muted-foreground">
        {t("settings.buildInfo")}: {t("settings.buildGit")} {BUILD_INFO.gitSha} · {t("settings.buildCheckpoint")} {BUILD_INFO.checkpointId} · {t("settings.buildTime")} {new Date(BUILD_INFO.buildTime).toLocaleString()}
      </p> : null}
      </> : null}

      {isBusinessWorkspace && activeTab === "entities" ? <EntityManagement embedded /> : null}
      {isIndividualWorkspace && activeTab === "balance" ? <AccountReconciliationPanel /> : null}
      {activeTab === "fx" ? <FxRateManagement embedded /> : null}

      {activeTab === "privacy" ? <>

      {/* Export Data Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            {t("account.exportAllTitle")}
          </CardTitle>
          <CardDescription>
            {t("account.exportAllDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleExportJSON}
              disabled={exportLoading}
              variant="outline"
              className="flex items-center gap-2"
            >
              <FileJson className="w-4 h-4" />
              {exportLoading
                ? (isRTL ? "جارٍ التحميل..." : "Loading...")
                : t("account.exportAllJson")}
            </Button>
            <Button
              onClick={handleExportCSV}
              disabled={exportLoading}
              variant="outline"
              className="flex items-center gap-2"
            >
              <FileSpreadsheet className="w-4 h-4" />
              {exportLoading
                ? (isRTL ? "جارٍ التحميل..." : "Loading...")
                : t("account.exportAllCsv")}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("account.exportAllHint")}
          </p>
        </CardContent>
      </Card>

      {isBusinessWorkspace ? <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="w-5 h-5" />
            {t("businessDataClear.title")}
          </CardTitle>
          <CardDescription>{t("businessDataClear.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!showBusinessClearConfirm ? <Button type="button" variant="destructive" className="min-h-11 gap-2" onClick={() => setShowBusinessClearConfirm(true)}>
            <Trash2 className="w-4 h-4" />
            {t("businessDataClear.start")}
          </Button> : <div className="space-y-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <div className="min-w-0 space-y-2">
                <p className="font-semibold text-destructive">{t("businessDataClear.warning")}</p>
                <p className="text-sm font-medium">{t("businessDataClear.previewTitle")}</p>
                {businessClearPreviewLoading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : businessClearPreview ? <>
                  <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2" aria-label={t("businessDataClear.previewTitle")}>
                    {Object.entries(businessClearPreview.counts).map(([key, count]) => <div key={key} className="flex items-center justify-between gap-3 rounded-md border border-border bg-background/60 px-3 py-2">
                      <span className="text-muted-foreground">{t(`businessDataClear.${key}`)}</span>
                      <span className="shrink-0 font-semibold tabular-nums">{count}</span>
                    </div>)}
                  </div>
                  <p className="text-sm text-muted-foreground">{t("businessDataClear.retained")}</p>
                  <div className="space-y-2">
                    <Label htmlFor="business-clear-name">{t("businessDataClear.typeExact")}</Label>
                    <p className="break-words rounded-md bg-background/60 px-3 py-2 font-mono text-sm" dir="ltr">{businessClearPreview.entity.name}</p>
                    <Input id="business-clear-name" aria-label={t("businessDataClear.confirmInput")} value={businessClearName} onChange={(event) => setBusinessClearName(event.target.value)} autoComplete="off" />
                  </div>
                </> : <p className="text-sm text-destructive">{t("businessDataClear.failed")}</p>}
              </div>
            </div>
            <div className="flex flex-col-reverse gap-3 sm:flex-row">
              <Button type="button" variant="outline" className="min-h-11" onClick={() => { setShowBusinessClearConfirm(false); setBusinessClearName(""); }} disabled={clearBusinessData.isPending}>{t("businessDataClear.cancel")}</Button>
              <Button type="button" variant="destructive" className="min-h-11 gap-2" onClick={confirmBusinessDataClear} disabled={!businessClearPreview || businessClearName !== businessClearPreview.entity.name || clearBusinessData.isPending}>
                {clearBusinessData.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {clearBusinessData.isPending ? t("businessDataClear.clearing") : t("businessDataClear.confirm")}
              </Button>
            </div>
          </div>}
        </CardContent>
      </Card> : null}

      {/* Delete Account Section */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="w-5 h-5" />
            {isRTL ? "حذف الحساب" : "Delete Account"}
          </CardTitle>
          <CardDescription>
            {isRTL
              ? "سيؤدي هذا إلى حذف حسابك وجميع بياناتك بشكل دائم. لا يمكن التراجع عن هذا الإجراء."
              : "This will permanently delete your account and all your data. This action cannot be undone."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!showDeleteConfirm ? (
            <Button
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              {isRTL ? "أريد حذف حسابي" : "I want to delete my account"}
            </Button>
          ) : (
            <div className="space-y-4 border border-destructive/30 rounded-lg p-4 bg-destructive/5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <p className="font-semibold text-destructive">
                    {isRTL ? "تحذير: هذا الإجراء لا يمكن التراجع عنه" : "Warning: This action is irreversible"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {isRTL ? "سيتم حذف ما يلي نهائياً:" : "The following will be permanently deleted:"}
                  </p>
                  <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
                    <li>
                      {isRTL ? "ملفك الشخصي ومعلومات الحساب" : "Your profile and account information"}
                    </li>
                    <li>
                      {isRTL
                        ? `${deletionSummary?.transactions ?? "..."} معاملة`
                        : `${deletionSummary?.transactions ?? "..."} transactions`}
                    </li>
                    <li>
                      {isRTL
                        ? `${deletionSummary?.budgets ?? "..."} ميزانية`
                        : `${deletionSummary?.budgets ?? "..."} budgets`}
                    </li>
                    <li>
                      {isRTL
                        ? `${deletionSummary?.bankSenders ?? "..."} مرسل بنكي`
                        : `${deletionSummary?.bankSenders ?? "..."} bank senders`}
                    </li>
                    <li>
                      {isRTL
                        ? `${deletionSummary?.parsingErrors ?? "..."} سجل أخطاء تحليل`
                        : `${deletionSummary?.parsingErrors ?? "..."} parsing error records`}
                    </li>
                    <li>
                      {isRTL
                        ? "جميع الملفات المرفوعة (كشوفات PDF، صور)"
                        : "All uploaded files (PDF statements, images)"}
                    </li>
                  </ul>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">
                  {isRTL
                    ? "اكتب DELETE للتأكيد:"
                    : "Type DELETE to confirm:"}
                </p>
                <Input
                  value={confirmWord}
                  onChange={(e) => setConfirmWord(e.target.value)}
                  placeholder="DELETE"
                  className="max-w-xs font-mono"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={confirmWord !== "DELETE" || deleteAccount.isPending}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  {deleteAccount.isPending
                    ? (isRTL ? "جارٍ الحذف..." : "Deleting...")
                    : (isRTL ? "حذف حسابي نهائياً" : "Permanently Delete My Account")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setConfirmWord("");
                  }}
                >
                  {isRTL ? "إلغاء" : "Cancel"}
                </Button>
              </div>

              {deleteAccount.error && (
                <p className="text-sm text-destructive">
                  {deleteAccount.error.message}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      </> : null}
    </div>
  );
}
